<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import templatesUploadApi from 'dashboard/api/account/uploads';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  inboxId: {
    type: Number,
    default: null,
  },
  initialParams: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const isUploadingFile = ref(false);
const fileInputRef = ref(null);
const selectedFileName = ref('');
const imagePreviewError = ref('');
const isImageFromUpload = ref(false);
const isFileUploaded = ref(false);
const previewImageUrl = ref('');
const manualUrlInput = ref('');

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  const params = buildTemplateParameters(props.template, hasMediaHeader.value);

  if (props.initialParams) {
    if (props.initialParams.body && params.body) {
      Object.keys(params.body).forEach(key => {
        if (props.initialParams.body[key] !== undefined) {
          params.body[key] = props.initialParams.body[key];
        }
      });
    }
    if (props.initialParams.header && params.header) {
      Object.assign(params.header, props.initialParams.header);
    }
    if (props.initialParams.buttons && params.buttons) {
      params.buttons.forEach((btn, idx) => {
        if (props.initialParams.buttons[idx]?.parameter) {
          btn.parameter = props.initialParams.buttons[idx].parameter;
        }
      });
    }
  }

  processedParams.value = params;
  selectedFileName.value = '';
  isImageFromUpload.value = false;
  isFileUploaded.value = false;
  previewImageUrl.value = '';
  manualUrlInput.value = '';
  imagePreviewError.value = '';

  if (props.initialParams?.header?.media_url) {
    previewImageUrl.value = props.initialParams.header.media_url;
    isFileUploaded.value = true;
  }
};

const updateManualUrl = value => {
  manualUrlInput.value = value;
  imagePreviewError.value = '';

  if (!value) {
    previewImageUrl.value = '';
    return;
  }

  try {
    const parsed = new URL(value);

    if (
      headerComponent.value?.format &&
      headerComponent.value.format.toLowerCase().includes('image') &&
      parsed.pathname.toLowerCase().endsWith('.webp')
    ) {
      imagePreviewError.value =
        t('WHATSAPP_TEMPLATES.PARSER.WEBP_NOT_SUPPORTED') ||
        'Formato WEBP não é aceito pelo WhatsApp.';
      previewImageUrl.value = '';
      return;
    }

    previewImageUrl.value = value;
  } catch {
    imagePreviewError.value =
      t('WHATSAPP_TEMPLATES.PARSER.INVALID_URL') || 'URL inválida';
    previewImageUrl.value = '';
  }
};

const handleImageError = () => {
  imagePreviewError.value =
    t('WHATSAPP_TEMPLATES.PARSER.IMAGE_LOAD_ERROR') ||
    'Erro ao carregar a imagem. Verifique a URL.';
  previewImageUrl.value = '';
  isImageFromUpload.value = false;
};

const handleImageLoad = () => {
  imagePreviewError.value = '';
  processedParams.value.header ??= {};
  if (isImageFromUpload.value) {
    return;
  }

  processedParams.value.header.media_url =
    manualUrlInput.value || previewImageUrl.value || '';
};

const showPreview = computed(() => {
  if (!previewImageUrl.value) return false;
  if (isImageFromUpload.value) return isFileUploaded.value;
  return !isFileUploaded.value;
});

const onFileSelected = async e => {
  const file = e.target.files && e.target.files[0];
  if (!file) return;

  selectedFileName.value = file.name;
  const declaredFormat = headerComponent.value?.format || null;
  const mediaType = declaredFormat
    ? declaredFormat.toString().toLowerCase()
    : null;

  const isImage = file.type && file.type.startsWith('image/');
  const isVideo = file.type && file.type.startsWith('video/');
  const isDocument = mediaType === 'document' || (!isImage && !isVideo);
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
  const MAX_VIDEO_SIZE = 16 * 1024 * 1024;
  const MAX_DOCUMENT_SIZE = 100 * 1024 * 1024;
  let validationError = '';

  if (isImage) {
    if (!['image/jpeg', 'image/png'].includes(file.type.toLowerCase())) {
      validationError =
        t('WHATSAPP_TEMPLATES.PARSER.INVALID_IMAGE_TYPE') ||
        'Apenas imagens JPG, JPEG e PNG são permitidas.';
    } else if (file.size > MAX_IMAGE_SIZE) {
      validationError =
        t('WHATSAPP_TEMPLATES.PARSER.IMAGE_TOO_LARGE', { size: 5 }) ||
        'O tamanho da imagem excede o limite permitido de 5MB.';
    }
  } else if (isVideo) {
    if (!['video/mp4', 'video/3gpp'].includes(file.type.toLowerCase())) {
      validationError =
        t('WHATSAPP_TEMPLATES.PARSER.INVALID_VIDEO_TYPE') ||
        'Apenas vídeos MP4 e 3GPP são permitidos.';
    } else if (file.size > MAX_VIDEO_SIZE) {
      validationError =
        t('WHATSAPP_TEMPLATES.PARSER.VIDEO_TOO_LARGE', { size: 16 }) ||
        'O tamanho do vídeo excede o limite permitido de 16MB.';
    }
  } else if (isDocument && file.size > MAX_DOCUMENT_SIZE) {
    validationError =
      t('WHATSAPP_TEMPLATES.PARSER.FILE_TOO_LARGE', { size: 100 }) ||
      'O documento excede o limite permitido de 100MB.';
  }

  if (validationError) {
    imagePreviewError.value = validationError;
    useAlert(validationError);
    selectedFileName.value = '';
    isImageFromUpload.value = false;
    isFileUploaded.value = false;
    try {
      if (fileInputRef.value) fileInputRef.value.value = null;
    } catch (_e) {
      // ignore file input reset error
    }
    return;
  }

  isUploadingFile.value = true;
  try {
    const response = await templatesUploadApi.upload(
      props.inboxId,
      file,
      mediaType
    );
    const { url } = response.data;

    processedParams.value.header ??= {};
    processedParams.value.header.media_url = url;

    isFileUploaded.value = true;
    manualUrlInput.value = '';
    imagePreviewError.value = '';

    const isHeaderImage =
      headerComponent.value?.format &&
      headerComponent.value.format.toLowerCase().includes('image');
    if (
      isHeaderImage &&
      file.type &&
      file.type.toLowerCase().startsWith('image')
    ) {
      isImageFromUpload.value = true;
      const reader = new FileReader();
      reader.onload = event => {
        previewImageUrl.value = event.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      isImageFromUpload.value = false;
      previewImageUrl.value = '';
    }

    useAlert(
      t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_SUCCESS') ||
        'Arquivo enviado com sucesso'
    );
  } catch (error) {
    selectedFileName.value = '';
    isImageFromUpload.value = false;
    previewImageUrl.value = '';
    isFileUploaded.value = false;
    const errorMessage =
      error?.response?.data?.error ||
      t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_ERROR') ||
      'Falha ao enviar o arquivo';
    useAlert(errorMessage);
  } finally {
    try {
      if (fileInputRef.value) fileInputRef.value.value = null;
    } catch (_e) {
      // ignore file input reset error
    }
    isUploadingFile.value = false;
  }
};

const triggerFileSelect = () => {
  if (fileInputRef.value) fileInputRef.value.click();
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

watch(
  () => props.initialParams,
  newParams => {
    if (newParams) {
      initializeTemplateParameters();
    }
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>

        <div v-if="!isFileUploaded" class="flex items-center mb-2.5">
          <Input
            :model-value="manualUrlInput"
            type="url"
            class="flex-1 mr-2"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateManualUrl"
          />

          <Button
            v-if="!isUploadingFile"
            class="ml-2"
            :label="$t('WHATSAPP_TEMPLATES.PARSER.UPLOAD_BUTTON') || 'Upload'"
            @click.prevent="triggerFileSelect"
          />

          <span v-else class="text-sm text-n-slate-10 ml-2">{{
            $t('WHATSAPP_TEMPLATES.PARSER.UPLOADING') || 'Enviando...'
          }}</span>
        </div>

        <input
          ref="fileInputRef"
          type="file"
          class="hidden"
          :accept="
            headerComponent?.format?.toLowerCase() === 'document'
              ? 'text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/pdf'
              : headerComponent?.format?.toLowerCase() === 'video'
                ? 'video/3gpp,video/mp4'
                : headerComponent?.format?.toLowerCase() === 'image'
                  ? 'image/jpeg,image/png'
                  : '*/*'
          "
          :disabled="isUploadingFile"
          @change="onFileSelected"
        />

        <div
          v-if="isFileUploaded && selectedFileName"
          class="mb-2.5 p-2 rounded-md bg-n-slate-2"
        >
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2 flex-1 min-w-0">
              <span class="font-medium text-sm text-n-slate-11">{{
                $t('WHATSAPP_TEMPLATES.PARSER.FILE_SELECTED') ||
                'Arquivo selecionado:'
              }}</span>
              <span
                class="text-n-slate-12 ml-1 truncate"
                :title="selectedFileName"
              >
                {{ selectedFileName }}
              </span>
            </div>

            <Button
              class="text-xs flex-shrink-0"
              :label="
                $t('WHATSAPP_TEMPLATES.PARSER.CHANGE_FILE') || 'Trocar arquivo'
              "
              @click.prevent="triggerFileSelect"
            />
          </div>
        </div>

        <div
          v-if="imagePreviewError && !isFileUploaded"
          class="mb-2.5 p-2 rounded-md bg-n-ruby-9/20"
        >
          <p class="text-sm text-n-ruby-9">{{ imagePreviewError }}</p>
        </div>

        <div v-if="showPreview" class="mb-2.5">
          <div class="rounded-md overflow-hidden bg-n-slate-2 p-2">
            <img
              :src="previewImageUrl"
              :alt="selectedFileName || 'Preview'"
              class="max-w-full max-h-64 rounded-md"
              @error="handleImageError"
              @load="handleImageLoad"
            />
          </div>
        </div>
      </div>

      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
